import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../lib/store';
import type { BatchHatchResponse, BatchHatchResult } from '../../lib/types';
import { Button, Modal } from '../ui';

/**
 * The hatch review — design PART 28.
 *
 * A hardcore breeder cracks twenty eggs at a time and needs to know, in one
 * glance, whether any of them mattered. So the summary leads and the grid
 * follows, sorted with the interesting results first — a Shiny at the bottom of
 * an alphabetical list is a Shiny somebody scrolls past.
 *
 * The rule that shapes everything else here is PART 28's last paragraph: no
 * one-click destructive release of anything rare. This screen cannot release a
 * protected Pokemon at all. Protection is applied server-side at hatch (see
 * `game/breeding/protection.ts`) rather than decided here, so a player who
 * never opens this screen is just as safe as one who does — this is a *review*,
 * not the thing keeping their Shiny.
 */

interface Props {
  batch: BatchHatchResponse;
  onClose: () => void;
  onChanged: () => void;
}

/** Interesting first: shiny, then protected, then new species, then the rest. */
function rank(row: BatchHatchResult): number {
  if (row.pokemon.isShiny) return 0;
  if (row.protectedFor.length > 0) return 1;
  if (row.isNewSpecies) return 2;
  return 3;
}

export function HatchReview({ batch, onClose, onChanged }: Props) {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);
  const [released, setReleased] = useState<Set<string>>(new Set());
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const rows = [...batch.results].sort((a, b) => rank(a) - rank(b));
  const { summary } = batch;

  // Only ever the plain ones. A protected result is not merely excluded from
  // "select all" — it can never enter this set at all, so there is no code path
  // from this screen to releasing one.
  const releasable = rows.filter(
    (row) => row.protectedFor.length === 0 && !released.has(row.pokemon.id),
  );

  function toggle(row: BatchHatchResult) {
    if (row.protectedFor.length > 0) return;
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(row.pokemon.id)) next.delete(row.pokemon.id);
      else next.add(row.pokemon.id);
      return next;
    });
  }

  async function sendToProfessor() {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      let done = 0;
      for (const id of picked) {
        try {
          await api.post(`/pokemon/${id}/release`, {});
          done++;
        } catch {
          // One refusal (the last of its species, say) must not stop the rest.
          continue;
        }
      }
      setReleased((current) => new Set([...current, ...picked]));
      setPicked(new Set());
      push('success', `Sent ${done} to the Professor.`, '◆');
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not send those.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} wide labelledBy="hatch-review-title">
      <div className="stack">
        <h2 id="hatch-review-title" className="page-head__title">
          {summary.hatched} eggs hatched
        </h2>
        {/* --- The headline. ------------------------------------------------ */}
        <div className="row row--wrap" style={{ gap: 6 }}>
          <span className="chip">{summary.ordinary} ordinary</span>
          {summary.rare > 0 && (
            <span className="chip" style={{ background: 'var(--sky)', color: '#082a3a' }}>
              {summary.rare} rare
            </span>
          )}
          {summary.shinies > 0 && (
            <span className="chip" style={{ background: 'var(--gold)', color: '#3a2a06' }}>
              ✦ {summary.shinies} shiny
            </span>
          )}
          {summary.newSpecies > 0 && <span className="chip">{summary.newSpecies} new to the dex</span>}
          <span className="chip">+{summary.coinsEarned.toLocaleString()} coins</span>
        </div>

        {/* --- The results. -------------------------------------------------- */}
        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))', gap: 8 }}
        >
          {rows.map((row) => (
            <ResultCard
              key={row.pokemon.id}
              row={row}
              gone={released.has(row.pokemon.id)}
              picked={picked.has(row.pokemon.id)}
              onToggle={() => toggle(row)}
            />
          ))}
        </div>

        {/* --- Bulk actions. ------------------------------------------------- */}
        <div className="row row--between row--wrap" style={{ gap: 6 }}>
          <div className="row" style={{ gap: 6 }}>
            <Button
              size="sm"
              disabled={busy || releasable.length === 0}
              onClick={() => setPicked(new Set(releasable.map((row) => row.pokemon.id)))}
            >
              Select {releasable.length} ordinary
            </Button>
            {picked.size > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
                Clear
              </Button>
            )}
          </div>
          <div className="row" style={{ gap: 6 }}>
            <Button
              size="sm"
              disabled={busy || picked.size === 0}
              loading={busy}
              onClick={() => void sendToProfessor()}
            >
              Send {picked.size} to the Professor
            </Button>
            <Button variant="primary" size="sm" onClick={onClose}>
              Keep the rest
            </Button>
          </div>
        </div>

        <p className="tiny muted">
          Anything Shiny, rare or from a bloodline is locked automatically and cannot be sent from
          this screen. Change what gets protected in Settings.
        </p>
      </div>
    </Modal>
  );
}

function ResultCard({
  row,
  gone,
  picked,
  onToggle,
}: {
  row: BatchHatchResult;
  gone: boolean;
  picked: boolean;
  onToggle: () => void;
}) {
  const locked = row.protectedFor.length > 0;
  const { pokemon } = row;

  return (
    <div
      className={`pxframe hatch-result ${pokemon.isShiny ? 'hatch-result--shiny' : ''} ${
        picked ? 'hatch-result--picked' : ''
      } ${gone ? 'hatch-result--gone' : ''}`}
    >
      <button
        type="button"
        className="hatch-result__pick"
        onClick={onToggle}
        disabled={locked || gone}
        title={locked ? `Protected: ${row.protectedFor.join(', ')}` : 'Select to send away'}
      >
        <img className="pixel" src={pokemon.sprite} alt="" loading="lazy" />
      </button>

      <Link className="hatch-result__name" to={`/pokemon/${pokemon.id}`}>
        {pokemon.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
        {pokemon.displayName}
      </Link>

      <div className="row row--wrap tiny" style={{ gap: 3 }}>
        {row.isNewSpecies && <span className="chip chip--sm">New!</span>}
        {row.sizeTitle && <span className="chip chip--sm">{row.sizeTitle}</span>}
        {!row.sizeTitle && row.sizeLabel && row.sizeLabel !== 'Average' && (
          <span className="chip chip--sm">{row.sizeLabel}</span>
        )}
        {row.markingName && <span className="chip chip--sm">{row.markingName}</span>}
        {row.generation > 0 && <span className="chip chip--sm">Gen {row.generation}</span>}
      </div>

      {gone ? (
        <div className="tiny muted">Sent to the Professor</div>
      ) : locked ? (
        <div className="tiny muted" title={row.protectedFor.join(', ')}>
          🔒 {row.protectedFor[0]}
        </div>
      ) : null}
    </div>
  );
}
